import React, { FC, useEffect, useState } from "react";
import { Heading } from "@digipost/dds-heading-react";
import illustration from "../../assets/illustrations/store_bedrifter.svg";
import styles from "./ManuellRegistreringKontaktinfo.module.less";
import Translated from "../infrastructure/translation/Translated";
import { Input } from "@digipost/dds-input-react";
import { useTranslate } from "../infrastructure/translation/useTranslate";
import {
  ButtonGroup,
  PrimaryButton,
  SecondaryButton,
} from "@digipost/dds-buttons-react";
import { useNavigate, useSearchParams } from "react-router";
import { Banner } from "@digipost/dds-banner-react";
import {
  emailValidator,
  namesValidator,
  orgNrValidator,
} from "../infrastructure/validation/validation";
import { ManuellKontaktinfo } from "../Registreringsflyt/useRegistrerVirksomhet";
import { useHentVirksomhetInfo } from "../Registreringsflyt/useHentVirksomhetInfo";
import { useManuellRegistreringContext } from "../ManuellRegistreringsflyt/useManuellRegistreringContext";
import { Route } from "../infrastructure/routing/routes";

const oversettApp = (verdi: string) => {
  return verdi === "DIGIPOST" ? "Digipost" : "Posten Signering";
};

const ManuellRegistreringKontaktinfo: FC = (): React.ReactElement => {
  const navigate = useNavigate();
  const translate = useTranslate();

  const { hentVirksomhetInfo } = useHentVirksomhetInfo();

  const [searchParams] = useSearchParams();

  const eksisterendeRegistrering = searchParams.get("eksisterende");
  const prefillOrgNr = searchParams.get("orgNr");

  const { kontaktInfo, submitKontaktinfo } = useManuellRegistreringContext();

  const [orgNumber, setOrgNumber] = useState<string>(
    prefillOrgNr || kontaktInfo?.orgNumber || "",
  );
  const [orgName, setOrgName] = useState<string>("");

  const [firstName, setFirstName] = useState<string>(
    kontaktInfo?.firstName || "",
  );
  const [lastName, setLastName] = useState<string>(kontaktInfo?.lastName || "");

  const [companyEmail, setCompanyEmail] = useState<string>(
    kontaktInfo?.companyEmail || "",
  );
  const [personalEmail, setPersonalEmail] = useState<string>(
    kontaktInfo?.personalEmail || "",
  );

  const [companyEmailValid, setCompanyEmailValid] = useState<boolean>(true);
  const [personalEmailValid, setPersonalEmailValid] = useState<boolean>(true);

  const [comment, setComment] = useState<string>(kontaktInfo?.comment || "");

  const [orgnrValid, setOrgnrValid] = useState<boolean>(true);
  const [virksomhetsnavnValid, setVirksomhetsnavnValid] =
    useState<boolean>(true);
  const [fornavnValid, setFornavnValid] = useState<boolean>(true);
  const [etternavnValid, setEtternavnValid] = useState<boolean>(true);

  useEffect(() => {
    if (orgNrValidator(orgNumber)) {
      hentVirksomhetInfo(orgNumber).then((res) => {
        if (res != undefined) {
          setOrgName(res.orgName);
        }
      });
    }
  }, [orgNumber, hentVirksomhetInfo]);

  const visAlleredeEksisterendeBanner = eksisterendeRegistrering != null;

  const goBack = () => {
    navigate(`../../${Route.Registrering}/velg-virksomhet`);
  };

  const submitForm = () => {
    const validOrgnr = orgNrValidator(orgNumber);
    setOrgnrValid(validOrgnr);

    const validVirksomhetsnavn = namesValidator(orgName);
    setVirksomhetsnavnValid(validVirksomhetsnavn);

    const validFornavn = namesValidator(firstName);
    setFornavnValid(validFornavn);

    const validEtternavn = namesValidator(lastName);
    setEtternavnValid(validEtternavn);

    const validPersonalEmail = emailValidator(personalEmail);
    setPersonalEmailValid(validPersonalEmail);

    const validCompanyEmail = emailValidator(companyEmail);
    setCompanyEmailValid(validCompanyEmail);

    if (
      validOrgnr &&
      validVirksomhetsnavn &&
      validFornavn &&
      validEtternavn &&
      validPersonalEmail &&
      validCompanyEmail
    ) {
      const kontaktInfo: ManuellKontaktinfo = {
        orgNumber,
        orgName,
        firstName,
        lastName,
        companyEmail,
        personalEmail,
        comment,
      };

      submitKontaktinfo(kontaktInfo);
    } else {
      return;
    }
  };

  return (
    <section>
      <img src={illustration} alt="" />
      <Heading level={1} size={"large"} className={styles.heading}>
        <Translated textKey={"manual.header"} />
      </Heading>
      {visAlleredeEksisterendeBanner && (
        <Banner variant={"warning"} role={"alert"}>
          <Translated
            textKey={"virksomhet.registrer.allerede-registrert"}
            params={{ eksisterende: oversettApp(eksisterendeRegistrering) }}
          />
        </Banner>
      )}
      <p>
        <Translated textKey={"manual.intro"} />
      </p>
      <p>
        <Translated textKey={"manual.intro_contd"} />
      </p>
      <form className={styles.wrapper}>
        <fieldset>
          <Input
            name={"orgNumber"}
            inputType={"text"}
            labelText={translate("generic.orgnr")}
            value={orgNumber}
            valid={orgnrValid}
            errorText={orgnrValid ? "" : translate("validation.orgnr")}
            onChange={(e) => setOrgNumber(e.target.value)}
          />
          <Input
            name={"name"}
            inputType={"text"}
            labelText={translate("manual.business_name")}
            value={orgName}
            valid={virksomhetsnavnValid}
            errorText={
              virksomhetsnavnValid ? "" : translate("validation.business_name")
            }
            onChange={(e) => setOrgName(e.target.value)}
          />
          <Input
            name={"companyEmail"}
            inputType={"email"}
            labelText={translate("contact.email_company")}
            autoComplete={"email"}
            value={companyEmail}
            valid={companyEmailValid}
            errorText={companyEmailValid ? "" : translate("validation.email")}
            onChange={(e) => setCompanyEmail(e.target.value)}
          />
        </fieldset>

        <fieldset>
          <Input
            name={"givenName"}
            inputType={"text"}
            labelText={translate("manual.first_name")}
            value={firstName}
            valid={fornavnValid}
            errorText={fornavnValid ? "" : translate("validation.person_name")}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <Input
            name={"surname"}
            inputType={"text"}
            labelText={translate("manual.surname")}
            value={lastName}
            valid={etternavnValid}
            errorText={
              etternavnValid ? "" : translate("validation.person_name")
            }
            onChange={(e) => setLastName(e.target.value)}
          />
          <Input
            name={"personalEmail"}
            inputType={"email"}
            labelText={translate("contact.email_personal")}
            autoComplete={"email"}
            value={personalEmail}
            valid={personalEmailValid}
            errorText={personalEmailValid ? "" : translate("validation.email")}
            onChange={(e) => setPersonalEmail(e.target.value)}
          />
        </fieldset>
        <fieldset>
          <Input
            name={"comment"}
            inputType={"text"}
            labelText={translate("manual.comment")}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </fieldset>
        <ButtonGroup>
          <SecondaryButton onClick={goBack} type={"button"}>
            <Translated textKey={"generic.back"} />
          </SecondaryButton>
          <PrimaryButton
            onClick={(e) => {
              e.preventDefault();
              submitForm();
            }}
          >
            <Translated textKey={"generic.next"} />
          </PrimaryButton>
        </ButtonGroup>
      </form>
    </section>
  );
};

export default ManuellRegistreringKontaktinfo;
